/**
 * Local time-allowance shape and helpers — kept here (not in @wg/types/billing)
 * because they're a *derivation* of the wire types (BalanceResponse), not
 * additional protocol surface. Mirrors apps/WunderGuide/types/allowance.ts.
 */

export interface TimeAllowance {
  amount: number;
  period: number;
  periodName?: string;
  used: number;
  resetsAt: Date;
  remaining: number;
  stackedCount?: number;
}

export type RingState = 'good' | 'low' | 'critical' | 'exhausted';

export function getRingState(
  effectiveBalance: number | null,
  hasUnmeteredAccess: boolean,
): RingState {
  if (hasUnmeteredAccess) return 'good';
  if (effectiveBalance === null || effectiveBalance <= 0) return 'exhausted';
  if (effectiveBalance < 60) return 'critical';
  if (effectiveBalance < 300) return 'low';
  return 'good';
}

export function calculateEffectiveBalance(
  availableSeconds: number | null | undefined,
  allowance?: TimeAllowance,
  hasUnmeteredAccess?: boolean,
): number | null {
  if (hasUnmeteredAccess) return Infinity;
  const credits = availableSeconds ?? null;
  if (!allowance) return credits;
  if (credits === null || credits === 0) return allowance.remaining;
  return Math.min(credits, allowance.remaining);
}

export function formatTimeRemaining(
  seconds: number | null | undefined,
): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) {
    return '—';
  }
  if (!Number.isFinite(seconds)) return '∞';
  if (seconds < 60) return `${Math.floor(seconds)}s`;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hours > 0) {
    if (minutes > 0) return `${hours}h ${minutes}m`;
    return `${hours}h`;
  }
  if (secs > 0 && minutes < 5) {
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}m`;
}

export function formatResetTime(resetsAt: Date): string | null {
  const diff = resetsAt.getTime() - Date.now();
  if (diff <= 0) return null;
  const hours = Math.floor(diff / 3_600_000);
  const minutes = Math.floor((diff % 3_600_000) / 60_000);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}
